/**
 * NorthernCanadaMap Component
 *
 * This component provides an interactive map of Northern Canada with weather station markers.
 * It includes features for location tracking, station filtering, and responsive design.
 *
 * Key Features:
 * - Interactive map with zoom and pan controls
 * - Weather station markers with status indicators
 * - User location tracking
 * - Station filtering by type and status
 * - Responsive design for different screen sizes
 * - Custom marker icons with dynamic sizing
 */

import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  Popup,
  useMapEvents,
  useMap,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import iconImage from "./weather-station(color).png";
import pinIcon from "./gps.png";
import { getStationIcon } from "./stationIcons";

import axios from "axios";
import { stationMetaData } from "../../utils/rawData";
import {
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Switch,
  Button,
  ButtonGroup,
  Chip,
} from "@mui/material";
import { useQuery, QueryClientProvider, useQueryClient } from "react-query";
import { useWeatherStationsMap } from "../../services/useWeatherStationsMap";
import { useTranslation } from "../../context/TranslationContext";
import {
  createUrlFriendlyName,
  findStationByNameOrId,
} from "../../utils/rawFunction";
const DEFAULT_CENTER = [69.49408333333334, -89.69515];
const DEFAULT_ZOOM = 4.5;

/**
 * Creates a custom pin icon with text label
 * @param {string} iconUrl - URL of the icon image
 * @param {string} text - Text to display below the icon
 * @param {string} color - Color of the text
 * @returns {L.DivIcon} - Leaflet div icon
 */
const createCustomPinIcon = (iconUrl, text = "", color = "black") => {
  const html = `
	  <div style='text-align: center; position: relative;'>
		<img src='${iconUrl}' style='width: 40px; height: 40px;' />
		<div style='font-size: 12px; color: ${color}; border: 2px dotted red; display: inline-block; padding: 2px 4px; white-space: nowrap;'>${text}</div>
	  </div>
	`;

  return L.divIcon({
    html: html,
    className: "custom-icon",
    iconSize: [40, 60], // Increase the iconSize height to accommodate the text box
    iconAnchor: [20, 60], // Adjust the iconAnchor to position the text box below the icon
  });
};

/**
 * UserLocationMarker Component
 * Displays the user's current location on the map
 * @param {boolean} isLocationEnabled - Whether location tracking is enabled
 */
const UserLocationMarker = ({ isLocationEnabled }) => {
  const [position, setPosition] = useState(null);
  const map = useMap();

  useEffect(() => {
    if (isLocationEnabled) {
      map.locate().on("locationfound", function (e) {
        setPosition(e.latlng);
        map.flyTo(e.latlng, 10);
      });
    } else {
      setPosition(null);
      map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM);
    }
  }, [map, isLocationEnabled]);

  return position === null ? null : (
    <Marker
      position={position}
      icon={createCustomPinIcon(pinIcon, "You are here", "red")}
    >
      <Popup>You are here</Popup>
    </Marker>
  );
};

/**
 * Main NorthernCanadaMap Component
 * Handles the map display, station markers, and user interactions
 */
const NorthernCanadaMap = () => {
  // Map controller for handling map events and updates
  const MapController = () => {
    const map = useMap();
    const { translate, currentLanguage } = useTranslation();

    useEffect(() => {
      if (map) {
        // Force a map refresh to ensure proper rendering
        setTimeout(() => {
          map.invalidateSize();
        }, 100);
      }
    }, [map]);

    return null;
  };

  const { translate } = useTranslation();
  const navigate = useNavigate();

  // Weather station data fetching
  const {
    data: weatherStations,
    isLoading,
    isFetching,
    error,
  } = useWeatherStationsMap({ enabled: true });

  // State management
  const [isLocationEnabled, setIsLocationEnabled] = useState(false);
  const mapRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);
  const [stationFilter, setStationFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track and update map zoom level
  const ZoomLevelUpdater = () => {
    const map = useMap();

    useEffect(() => {
      const updateZoom = () => {
        setCurrentZoom(map.getZoom());
      };

      map.on("zoomend", updateZoom);
      updateZoom();
      return () => {
        map.off("zoomend", updateZoom);
      };
    }, [map]);

    return null;
  };

  // Handle station click navigation
  const handleStationClick = (stationId) => {
    const station = findStationByNameOrId(stationId);
    if (station) {
      const urlFriendlyName = createUrlFriendlyName(station.name);
      navigate(`/${urlFriendlyName}`);
    }
  };

  // Toggle location tracking
  const handleLocationToggle = () => {
    setIsLocationEnabled(!isLocationEnabled);
  };

  // Handle station filter changes
  const handleFilterChange = (filterValue) => {
    setStationFilter(filterValue);
  };

  // Filter stations based on selected criteria
  const getFilteredStations = () => {
    if (!weatherStations) return [];
    if (stationFilter === "all") return weatherStations;

    return weatherStations.filter((station) => {
      // Filter by category (new approach using the category field)
      if (stationFilter === "Community-led")
        return station.category === "Community-led";
      if (stationFilter === "Research")
        return station.category === "Research" || station.category === "";
      if (stationFilter === "Government")
        return station.category === "Government";

      // Filter by operational status (kept for backward compatibility)
      if (stationFilter === "operational")
        return station.status === "Operational";

      return true;
    });
  };

  // Get filtered stations
  const filteredStations = getFilteredStations();

  // Handle map resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Error handling
  if (error)
    return (
      <Typography color="error">An error occurred: {error.message}</Typography>
    );

  // Loading state
  const showLoadingIndicator = isLoading || isFetching;

  /**
   * Creates a custom icon for weather stations with dynamic styling
   * @param {string} iconUrl - URL of the station icon
   * @param {string} stationName - Name of the station
   * @param {string} info - Additional station information
   * @param {boolean} hasError - Whether the station has an error
   * @param {string} status - Station status
   * @param {boolean} isECCC - Whether the station is an ECCC station
   * @param {boolean} isNTI - Whether the station is an NTI station
   * @returns {L.DivIcon} - Custom Leaflet icon
   */
  const createCustomIcon = (
    iconUrl,
    stationName,
    info,
    hasError = false,
    status,
    stationType
  ) => {
    let iconColor, statusText, textColor;
    let opacity = 1;

    // Get the appropriate icon configuration for the station type
    const stationIconConfig = getStationIcon(stationType);

    switch (status) {
      case "Non-Operational":
        statusText = "Non-Operational";
        textColor = "#ad4242";
        opacity = 0.6;
        iconColor = "#ad4242"; // Red
        break;
      case "Warning":
        iconColor = "#f2d666"; // Yellow
        statusText = "Warning";
        textColor = "#d4a012";
        break;
      case "Operational":
        iconColor = "#78a62b"; // Green
        statusText = info || "Operational";
        textColor = "blue";
        break;
      default:
        iconColor = "#E0E0E0"; // Grey
        statusText = "Unknown Status";
        textColor = "#666666";
        opacity = 0.7;
    }

    // If there's an error, override the status styling
    if (hasError) {
      iconColor = "#ad4242";
      statusText = "Error";
      textColor = "#ad4242";
      opacity = 0.6;
    }

    // Calculate size based on zoom level
    const baseSize = Math.max(20, Math.min(40, currentZoom * 5)); // Size between 20-40px
    const fontSize = Math.max(8, Math.min(12, currentZoom * 1.5)); // Font size between 8-12px
    const showDetails = currentZoom >= 4; // Match MapContainer minZoom so labels stay visible when zoomed out

    const html = `
      <div style='text-align: center; position: relative;'>
        <div style='position: relative; display: inline-block;'>
          <img 
            src='${stationIconConfig.icon}' 
            style='
              width: ${baseSize}px; 
              height: ${baseSize}px; 
              opacity: ${opacity};
              filter: drop-shadow(0 2px 2px rgba(0,0,0,0.2));
            '
          />
          ${
            (hasError || status === "Non-Operational") && showDetails
              ? `
            <div style="
              position: absolute;
              top: -5px;
              right: -5px;
              background-color: ${iconColor};
              color: white;
              border-radius: 50%;
              width: ${fontSize + 4}px;
              height: ${fontSize + 4}px;
              font-size: ${fontSize + 2}px;
              line-height: ${fontSize + 4}px;
              text-align: center;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            ">×</div>
          `
              : ""
          }
        </div>
        ${
          showDetails
            ? `
          <div style='
            font-size: ${fontSize}px; 
            color: #333;
            font-weight: 500;
            margin-top: 2px;
            text-shadow: 0 1px 2px rgba(255,255,255,0.8);
          '>
            ${translate(stationName)}
          </div>
          <div style='
            font-size: ${fontSize - 2}px; 
            color: ${textColor};
            background-color: rgba(255,255,255,0.9);
            padding: 2px 4px;
            border-radius: 8px;
            margin-top: 2px;
            font-weight: 500;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            display: inline-block;
            width: fit-content;
            margin-left: auto;
            margin-right: auto;
          '>
            ${translate(statusText)}
          </div>
        `
            : ""
        }
      </div>
    `;

    return L.divIcon({
      html: html,
      className: "custom-icon",
      iconSize: [baseSize * 1.5, showDetails ? baseSize * 2 : baseSize],
      iconAnchor: [baseSize * 0.75, showDetails ? baseSize : baseSize * 0.5],
      popupAnchor: [0, -baseSize],
    });
  };
  return (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        border: "1px solid black",
        "& .leaflet-container": {
          width: "100%",
          height: "100%",
        },
      }}
    >
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false} // Disable the default zoom control
        minZoom={3}
        maxZoom={10}
        scrollWheelZoom={true}
        ref={mapRef}
        whenReady={(map) => {
          map.target.invalidateSize();
        }}
      >
        <MapController />
        <ZoomLevelUpdater />

        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {filteredStations &&
          filteredStations.map((station) => (
            <Marker
              key={station.id}
              position={[station.lat, station.lon]}
              icon={createCustomIcon(
                iconImage,
                station.name,
                station.info,
                station.hasError,
                station.status,
                station.type
              )}
              eventHandlers={{ click: () => handleStationClick(station.id) }}
            >
              {/* <Popup>
                <div>
                  <p>Station: {station.name}</p>
                  <p>Community: {station.Community}</p>
                  <p>Status: {station.status}</p>
                  <p>Type: {station.type}</p>
                  <p>Location: {station.location}</p>
                  <p>{station.info || "Loading..."}</p>
                </div>
              </Popup> */}
            </Marker>
          ))}
        <UserLocationMarker isLocationEnabled={isLocationEnabled} />
        <ZoomControl position="bottomright" />
      </MapContainer>

      {/* Filter buttons at the top */}
      <Box
        sx={{
          position: "absolute",
          top: "10px",
          left: "10px",
          right: "10px",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "center",
        }}
      >
        {/* Filter buttons row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            justifyContent: "center",
          }}
        >
          <Button
            variant={stationFilter === "all" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("all")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "all" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "all" ? 2 : 1,
            }}
          >
            {translate("All")}
          </Button>

          <Button
            variant={
              stationFilter === "Community-led" ? "contained" : "outlined"
            }
            color="primary"
            size="small"
            onClick={() => handleFilterChange("Community-led")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "Community-led" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "Community-led" ? 2 : 1,
            }}
          >
            {translate("Community-led")}
          </Button>
          <Button
            variant={stationFilter === "Research" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("Research")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "Research" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "Research" ? 2 : 1,
            }}
          >
            {translate("Research")}
          </Button>
          <Button
            variant={stationFilter === "Government" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("Government")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "Government" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "Government" ? 2 : 1,
            }}
          >
            {translate("Government")}
          </Button>

          <Button
            variant={stationFilter === "operational" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("operational")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "operational" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "operational" ? 2 : 1,
            }}
          >
            {translate("Operational")}
          </Button>

          {/* <Button
            variant={
              stationFilter === "non-operational" ? "contained" : "outlined"
            }
            color="primary"
            size="small"
            onClick={() => handleFilterChange("non-operational")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "non-operational" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "non-operational" ? 2 : 1,
            }}
          >
            {translate("Non-Operational")}
          </Button> */}
        </Box>

        {/* Find My Location button - now centered below filters */}
        <Box
          sx={{
            backgroundColor: "white",
            padding: "6px 12px",
            borderRadius: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            userSelect: "none",
            transition: "background-color 0.2s",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
            },
            width: "auto",
            alignSelf: "center",
          }}
          onClick={handleLocationToggle}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              color: isLocationEnabled ? "primary.main" : "text.primary",
              fontSize: "0.75rem",
              mr: 1,
            }}
          >
            {translate("Find My Location")}
          </Typography>
          <Switch
            checked={isLocationEnabled}
            color="primary"
            size="small"
            sx={{
              "& .MuiSwitch-thumb": {
                backgroundColor: isLocationEnabled
                  ? "primary.main"
                  : "grey.400",
              },
              "& .MuiSwitch-track": {
                backgroundColor: isLocationEnabled
                  ? "primary.light"
                  : "grey.300",
              },
              transform: "scale(0.8)",
            }}
          />
        </Box>

        {/* Station count chip - also centered */}
        {stationFilter !== "all" && (
          <Chip
            label={`${filteredStations.length} ${translate("stations")}`}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => setStationFilter("all")}
            sx={{
              backgroundColor: "white",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
            }}
          />
        )}
      </Box>

      {showLoadingIndicator && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            padding: "20px",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={50} />
          <Box sx={{ mt: 2 }}>Loading...</Box>
        </Box>
      )}
    </Box>
  );
};

export default NorthernCanadaMap;

//cluster code

// import React, { useState, useEffect, useRef } from "react";
// import {
//   MapContainer,
//   TileLayer,
//   Marker,
//   ZoomControl,
//   Popup,
//   useMapEvents,
//   useMap,
// } from "react-leaflet";
// import MarkerClusterGroup from 'react-leaflet-cluster';
// import { useNavigate } from "react-router-dom";
// import "leaflet/dist/leaflet.css";
// import L from "leaflet";
// import iconImage from "./weather-station(color).png";
// import pinIcon from "./gps.png";
// import { stationMetaData } from "../../utils/rawData";
// import {
//   IconButton,
//   CircularProgress,
//   Box,
//   Typography,
//   Switch,
// } from "@mui/material";
// import { useWeatherStationsMap } from "../../services/useWeatherStationsMap";

// const DEFAULT_CENTER = [72.49408333333334, -81.69515];
// const DEFAULT_ZOOM = 5.2;

// const createCustomIcon = (iconUrl, stationName, info, hasError = false, status) => {
//   let iconColor, statusText, textColor;
//   let opacity = 1;

//   switch (status) {
//     case "Non-Operational":
//       iconColor = "#ad4242";
//       statusText = "Non-Operational";
//       textColor = "#ad4242";
//       opacity = 0.6;
//       break;
//     case "Warning":
//       iconColor = "#f2d666";
//       statusText = "Warning";
//       textColor = "#d4a012";
//       break;
//     case "Operational":
//       iconColor = "#78a62b";
//       statusText = info || "Operational";
//       textColor = "#2b5f1e";
//       break;
//     default:
//       iconColor = "#E0E0E0";
//       statusText = "Unknown Status";
//       textColor = "#666666";
//       opacity = 0.7;
//   }

//   if (hasError) {
//     iconColor = "#ad4242";
//     statusText = "Error";
//     textColor = "#ad4242";
//     opacity = 0.6;
//   }

//   const html = `
//     <div style='text-align: center; position: relative;'>
//       <div style='position: relative; display: inline-block;'>
//         <img
//           src='${iconUrl}'
//           style='
//             width: 40px;
//             height: 40px;
//             opacity: ${opacity};
//             filter: drop-shadow(0 2px 2px rgba(0,0,0,0.2));
//           '
//         />
//         ${hasError || status === "Non-Operational" ? `
//           <div style="
//             position: absolute;
//             top: -5px;
//             right: -5px;
//             background-color: ${iconColor};
//             color: white;
//             border-radius: 50%;
//             width: 20px;
//             height: 20px;
//             font-size: 16px;
//             line-height: 20px;
//             text-align: center;
//             box-shadow: 0 2px 4px rgba(0,0,0,0.2);
//           ">×</div>
//         ` : ''}
//       </div>
//       <div style='
//         font-size: 12px;
//         color: #333;
//         font-weight: 500;
//         margin-top: 2px;
//         text-shadow: 0 1px 2px rgba(255,255,255,0.8);
//       '>
//         ${stationName}
//       </div>
//       <div style='
//         font-size: 10px;
//         color: ${textColor};
//         background-color: rgba(255,255,255,0.9);
//         padding: 2px 6px;
//         border-radius: 10px;
//         margin-top: 2px;
//         font-weight: 500;
//         box-shadow: 0 1px 3px rgba(0,0,0,0.1);
//       '>
//         ${statusText}
//       </div>
//     </div>
//   `;

//   return L.divIcon({
//     html: html,
//     className: 'custom-icon',
//     iconSize: [60, 80],
//     iconAnchor: [30, 80],
//   });
// };

// const createCustomPinIcon = (iconUrl, text = "", color = "black") => {
//   const html = `
//     <div style='text-align: center; position: relative;'>
//       <img src='${iconUrl}' style='width: 40px; height: 40px;' />
//       <div style='
//         font-size: 12px;
//         color: ${color};
//         border: 2px dotted red;
//         display: inline-block;
//         padding: 2px 4px;
//         white-space: nowrap;
//         background-color: white;
//       '>${text}</div>
//     </div>
//   `;

//   return L.divIcon({
//     html: html,
//     className: "custom-icon",
//     iconSize: [40, 60],
//     iconAnchor: [20, 60],
//   });
// };
// const createClusterCustomIcon = (cluster) => {
//   const count = cluster.getChildCount();
//   const markers = cluster.getAllChildMarkers();

//   // Check cluster status
//   const hasNonOperational = markers.some(marker =>
//     marker.options.status === "Non-Operational" || marker.options.hasError
//   );
//   const hasWarning = !hasNonOperational && markers.some(marker =>
//     marker.options.status === "Warning"
//   );

//   let backgroundColor, textColor;
//   if (hasNonOperational) {
//     backgroundColor = '#ad4242';
//     textColor = '#ffffff';
//   } else if (hasWarning) {
//     backgroundColor = '#f2d666';
//     textColor = '#000000';
//   } else {
//     backgroundColor = '#78a62b';
//     textColor = '#ffffff';
//   }

//   return L.divIcon({
//     html: `
//       <div style="
//         background-color: ${backgroundColor};
//         color: ${textColor};
//         width: 36px;
//         height: 36px;
//         border-radius: 50%;
//         display: flex;
//         align-items: center;
//         justify-content: center;
//         font-size: 14px;
//         font-weight: bold;
//         border: 2px solid white;
//         box-shadow: 0 2px 5px rgba(0,0,0,0.2);
//       ">
//         ${count}
//       </div>
//     `,
//     className: 'custom-marker-cluster',
//     iconSize: [36, 36],
//     iconAnchor: [18, 18]
//   });
// };

// const UserLocationMarker = ({ isLocationEnabled }) => {
//   const [position, setPosition] = useState(null);
//   const map = useMap();

//   useEffect(() => {
//     if (isLocationEnabled) {
//       map.locate().on("locationfound", function (e) {
//         setPosition(e.latlng);
//         map.flyTo(e.latlng, 10);
//       });
//     } else {
//       setPosition(null);
//       map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM);
//     }
//   }, [map, isLocationEnabled]);

//   return position === null ? null : (
//     <Marker
//       position={position}
//       icon={createCustomPinIcon(pinIcon, "You are here", "red")}
//     >
//       <Popup>You are here</Popup>
//     </Marker>
//   );
// };

// const NorthernCanadaMap = () => {
//   const navigate = useNavigate();
//   const {
//     data: weatherStations,
//     isLoading,
//     isFetching,
//     error,
//   } = useWeatherStationsMap();
//   const [isLocationEnabled, setIsLocationEnabled] = useState(false);
//   const mapRef = useRef(null);

//   const handleStationClick = (stationId) => {
//     navigate(`/${stationId}`);
//   };

//   const handleLocationToggle = () => {
//     setIsLocationEnabled(!isLocationEnabled);
//   };

//   if (error) return (
//     <Typography color="error">An error occurred: {error.message}</Typography>
//   );

//   const showLoadingIndicator = isLoading || isFetching;

//   return (
//     <Box
//       sx={{
//         position: "relative",
//         height: "100vh",
//         width: "100%",
//         border: "1px solid black",
//       }}
//     >
//       <MapContainer
//         center={DEFAULT_CENTER}
//         zoom={DEFAULT_ZOOM}
//         style={{ height: "100%", width: "100%" }}
//         zoomControl={false}
//         minZoom={3}
//         maxZoom={10}
//         scrollWheelZoom={true}
//         ref={mapRef}
//       >
//         <TileLayer
//           url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
//           attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//         />

//         <MarkerClusterGroup
//           chunkedLoading
//           iconCreateFunction={createClusterCustomIcon}
//           maxClusterRadius={50}
//           spiderfyOnMaxZoom={true}
//           showCoverageOnHover={false}
//           zoomToBoundsOnClick={true}
//         >
//           {weatherStations &&
//             weatherStations.map((station) => (
//               <Marker
//                 key={station.id}
//                 position={[station.lat, station.lon]}
//                 icon={createCustomIcon(
//                   iconImage,
//                   station.name,
//                   station.info,
//                   station.hasError,
//                   station.status
//                 )}
//                 eventHandlers={{ click: () => handleStationClick(station.id) }}
//                 status={station.status}
//                 hasError={station.hasError}
//               >
//                 <Popup>
//                   <div style={{ minWidth: '200px' }}>
//                     <p><strong>Station:</strong> {station.name}</p>
//                     <p><strong>Community:</strong> {station.Community}</p>
//                     <p><strong>Status:</strong> {station.status}</p>
//                     <p><strong>Type:</strong> {station.type}</p>
//                     <p><strong>Location:</strong> {station.location}</p>
//                     <p>{station.info || "Loading..."}</p>
//                   </div>
//                 </Popup>
//               </Marker>
//             ))}
//         </MarkerClusterGroup>

//         <UserLocationMarker isLocationEnabled={isLocationEnabled} />
//         <ZoomControl position="bottomright" />
//       </MapContainer>

//       <Box
//         sx={{
//           position: "absolute",
//           top: "10px",
//           left: "10px",
//           zIndex: 1000,
//           backgroundColor: "white",
//           padding: "8px 12px",
//           borderRadius: "4px",
//           boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
//           display: "flex",
//           alignItems: "center",
//           cursor: "pointer",
//           userSelect: "none",
//           transition: "background-color 0.2s",
//           "&:hover": {
//             backgroundColor: "rgba(0, 0, 0, 0.1)",
//           },
//         }}
//         onClick={handleLocationToggle}
//       >
//         <Typography
//           variant="body2"
//           sx={{
//             mr: 1,
//             fontWeight: "bold",
//             color: isLocationEnabled ? "primary.main" : "text.primary",
//           }}
//         >
//           Find My Location
//         </Typography>
//         <Switch
//           checked={isLocationEnabled}
//           color="primary"
//           size="small"
//           sx={{
//             "& .MuiSwitch-thumb": {
//               backgroundColor: isLocationEnabled ? "primary.main" : "grey.400",
//             },
//             "& .MuiSwitch-track": {
//               backgroundColor: isLocationEnabled ? "primary.light" : "grey.300",
//             },
//           }}
//         />
//       </Box>

//       {showLoadingIndicator && (
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             zIndex: 1000,
//             backgroundColor: "rgba(255, 255, 255, 0.8)",
//             padding: "20px",
//             borderRadius: "10px",
//             display: "flex",
//             flexDirection: "column",
//             alignItems: "center",
//             justifyContent: "center",
//           }}
//         >
//           <CircularProgress size={50} />
//           <Box sx={{ mt: 2 }}>Loading...</Box>
//         </Box>
//       )}
//     </Box>
//   );
// };

// export default NorthernCanadaMap;
